import { useOnline } from "@vueuse/core";
import { io, Socket } from "socket.io-client";
import { ref } from "vue";

import useAuth from "@/composition/shared/useAuth.ts";
import { getAccessToken } from "@/utils/apiClients.ts";

export interface SocketEventListener {
  eventName: string;
  callback: (...param: any) => void;
}

const useAuthSockets = () => {
  const isConnected = ref(false);
  const isSocketAuthenticated = ref(false);
  const authError = ref<string | null>(null);
  const { isAuthenticated } = useAuth();

  // establishes the socket connection
  const initializeSocket = async (
    socketUrl: string,
    authSuccessCb?: () => void,
    errorCb?: (error: Error) => void,
    connectCb?: () => void,
  ): Promise<Socket | null> => {
    try {
      const token = await getAccessToken();

      const socket: Socket = io(socketUrl, {
        transports: ["polling", "websocket"],
        withCredentials: true,
        extraHeaders: {
          authorization: `Bearer ${token}`,
        },
      });

      setupSocketListeners(socket, authSuccessCb, errorCb, connectCb);
      return socket;
    } catch (err) {
      authError.value = `Failed to initialize socket: ${(err as Error).message}`;
      console.error(authError.value); // eslint-disable-line no-console
      if (errorCb) errorCb(err as Error);
      return null;
    }
  };

  const setupSocketListeners = (
    socket: Socket,
    authSuccessCb?: (message: string) => void,
    errorCb?: (error: Error) => void,
    connectCb?: () => void,
  ) => {
    socket.on("connect", () => {
      isConnected.value = true;
      if (connectCb) connectCb();
    });

    /**
     * authorization-required:
     * Attempting to obtain a fresh access token.
     * Emits an "authorization" event back to the server,
     * including the newly acquired token.
     */

    socket.on("authorization-required", async () => {
      try {
        const token = await getAccessToken();
        // the raw token needs to be send here
        socket.emit("authorization", token);
      } catch (err) {
        authError.value = `Failed to get access token: ${(err as Error).message}`;
        console.error(authError.value); // eslint-disable-line no-console
      }
    });

    /**
     * authorization-success:
     * update the application state upon successful authentication
     */
    socket.on("authorization-success", (message: string) => {
      isSocketAuthenticated.value = true;
      authError.value = null;
      if (authSuccessCb) authSuccessCb(message);
    });

    socket.on("authorization-failed", (reason: string) => {
      isSocketAuthenticated.value = false;
      authError.value = `Authorization failed: ${reason}`;
      console.error(authError.value); // eslint-disable-line no-console
    });

    socket.io.on("error", (err: Error) => {
      // Prevent Endless callLoop when user is Offline
      const isOnline = useOnline();
      if (isOnline.value && isAuthenticated.value) {
        socket.disconnect();
        if (errorCb) errorCb(err);
      }
    });
  };

  const addSocketListeners = (
    eventListeners: SocketEventListener[],
    socket: Socket | null,
  ) => {
    eventListeners.forEach((listener) => {
      socket?.on(listener.eventName, listener.callback);
    });
  };

  return {
    initializeSocket,
    isSocketAuthenticated,
    addSocketListeners,
  };
};

export default useAuthSockets;
